// src/features/professional/services/professionalPortfolioService.js
import { 
  doc, 
  getDoc,
  setDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const COLLECTION_NAME = 'professional_portfolios';

export const getUserPortfolio = async (userId) => {
  try {
    const portfolioRef = doc(db, COLLECTION_NAME, userId);
    const portfolioSnap = await getDoc(portfolioRef);
    
    if (portfolioSnap.exists()) {
      return portfolioSnap.data();
    } else {
      // Create empty portfolio structure
      const emptyPortfolio = {
        userId,
        qualifications: [],
        milestones: [],
        projects: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(portfolioRef, emptyPortfolio, { merge: true });
      return emptyPortfolio;
    }
  } catch (error) {
    console.error('Error fetching user portfolio:', error);
    throw error;
  }
};

export const createPortfolioItem = async (userId, itemType, itemData) => {
  try {
    const portfolioRef = doc(db, COLLECTION_NAME, userId);
    const portfolio = await getUserPortfolio(userId);
    const currentData = portfolio || { qualifications: [], milestones: [], projects: [] };

    const newItem = {
      id: `${itemType}_${Date.now()}`,
      ...itemData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const fieldName = itemType === 'qualification' ? 'qualifications' : 
                     itemType === 'milestone' ? 'milestones' : 'projects';

    const updatedData = {
      ...currentData,
      [fieldName]: [...(currentData[fieldName] || []), newItem],
      updatedAt: serverTimestamp()
    };

    await setDoc(portfolioRef, updatedData, { merge: true });
    return newItem.id;
  } catch (error) {
    console.error('Error creating portfolio item:', error);
    throw error;
  }
};

export const updatePortfolioItem = async (userId, itemType, itemId, itemData) => {
  try {
    const portfolioRef = doc(db, COLLECTION_NAME, userId);
    const currentData = await getUserPortfolio(userId);
    const fieldName = itemType === 'qualification' ? 'qualifications' : 
                     itemType === 'milestone' ? 'milestones' : 'projects';

    const items = currentData[fieldName] || [];
    const itemIndex = items.findIndex(item => item.id === itemId);
    
    if (itemIndex === -1) {
      throw new Error('Item not found');
    }

    items[itemIndex] = {
      ...items[itemIndex],
      ...itemData,
      updatedAt: serverTimestamp()
    };

    const updatedData = {
      ...currentData,
      [fieldName]: items,
      updatedAt: serverTimestamp()
    };

    await setDoc(portfolioRef, updatedData, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating portfolio item:', error);
    throw error;
  }
};

export const deletePortfolioItem = async (userId, itemType, itemId) => {
  try {
    const portfolioRef = doc(db, COLLECTION_NAME, userId);
    const currentData = await getUserPortfolio(userId);
    const fieldName = itemType === 'qualification' ? 'qualifications' : 
                     itemType === 'milestone' ? 'milestones' : 'projects';

    const items = currentData[fieldName] || [];
    const filteredItems = items.filter(item => item.id !== itemId);

    const updatedData = {
      ...currentData,
      [fieldName]: filteredItems,
      updatedAt: serverTimestamp()
    };

    await setDoc(portfolioRef, updatedData, { merge: true });
    return true;
  } catch (error) {
    console.error('Error deleting portfolio item:', error);
    throw error;
  }
};

export const getPortfolioStats = async (userId) => {
  try {
    const portfolio = await getUserPortfolio(userId);
    
    return {
      totalQualifications: portfolio.qualifications?.length || 0,
      totalMilestones: portfolio.milestones?.length || 0,
      totalProjects: portfolio.projects?.length || 0,
      completedProjects: portfolio.projects?.filter(p => p.status === 'Completed').length || 0,
      verifiedQualifications: portfolio.qualifications?.filter(q => q.verified).length || 0
    };
  } catch (error) {
    console.error('Error getting portfolio stats:', error);
    throw error;
  }
};
